import { Link } from 'react-router-dom';

import { JsonLd } from '@/components/JsonLd';
import { ROUTES } from '@/config/routes';
import { ChevronDown, MessageSquarePlus, Star } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// TÉMOIGNAGES — DÉSACTIVÉS TANT QUE NON COLLECTÉS + VALIDÉS PAR ÉCRIT
// ⚠️  Ne PAS réactiver sans :
//   1. Accord écrit signé de chaque personne citée (email suffit, à archiver).
//   2. Texte rédigé ou validé par la personne elle-même (pas par nous).
//   3. Mention de la fonction / club exacte (pas romancée).
// Réf. légale : Code conso. art. L.121-1 à L.121-4 + L.132-2-1 (faux avis = délit).
// ─────────────────────────────────────────────────────────────────────────────

const TESTIMONIALS = [
  /* {
    initials: 'JP',
    color: 'bg-emerald-600',
    name: 'Jean-Pierre Durand',
    role: 'Président, Club Pétanque Lyon 3',
    text: "Avant BoulesConnect, j'y passais mes week-ends. Maintenant je configure mon concours en 20 minutes et tout roule. Les joueurs adorent s'inscrire depuis leur téléphone.",
  },*/
  {
    initials: 'PC',
    color: 'bg-blue-600',
    name: 'Philippe Cochet',
    role: 'Organisateurs, SuperChallenge (provençal)',
    text: "Les organisateurs de nos concours étaient contraint de passer une semaine au téléphone pour les inscriptions. Nous avons décidés au Superchallenge de trouvé un logiciel d'inscription et de paiement en ligne après divers recherche nous avons découvert BoulesConnect (Madsaas) qui a résolu nos demandes dans un temps record. Depuis tous nos concours adhérents de notre association font les inscriptions en ligne. 32 concours Nationaux et régionaux au jeu provençal , plus aucun ne veut revenir à l'ancienne méthode.",
  },
  {
    initials: 'GV',
    color: 'bg-purple-600',
    name: 'Georges Ventre',
    role: 'Joueur, Organisateur, Marseille',
    text:
      "Votre outil nous apporte une entière satisfaction depuis plusieurs années. Il répond parfaitement à nos besoins en termes de fiabilité et de simplicité au quotidien. C’est exactement l'expérience que nous cherchons à offrir à nos utilisateurs.\n" +
      '\n' +
      'Je le recommande vivement.',
  },
];

const FAQ = [
  {
    q: 'Est-ce compliqué à prendre en main ?',
    a: 'Pas du tout. BoulesConnect a été conçu pour être intuitif. Vous pouvez créer votre premier concours en moins de 10 minutes. Des tutoriels et une aide en ligne sont disponibles, et notre équipe répond à vos questions par email.',
  },
  {
    q: "Que se passe-t-il si j'ai un problème le jour du concours ?",
    a: 'Notre équipe support répond par email aux heures ouvrées, du lundi au vendredi. La plateforme fonctionne en continu, 7j/7 et 24h/24, et la plupart des actions (inscriptions, paiements, résultats) restent opérationnelles sans intervention de notre part.',
  },
  {
    q: 'Mes données sont-elles sécurisées ?',
    a: 'Oui. Infrastructure européenne conforme RGPD, chiffrement en transit (HTTPS/TLS), mots de passe hachés, sauvegardes régulières. Les paiements sont traités par Stripe, acteur certifié PCI-DSS : vos coordonnées bancaires ne transitent jamais par nos serveurs. Vous pouvez exporter ou supprimer vos données à tout moment depuis votre espace personnel.',
  },
  {
    q: 'Puis-je annuler mon abonnement à tout moment ?',
    a: "Oui, sans frais ni justification. L'annulation est effective immédiatement depuis votre espace personnel. Vous conservez l'accès aux fonctionnalités payantes jusqu'à la fin de la période déjà réglée.",
  },
  {
    q: 'Les joueurs doivent-ils créer un compte ?',
    a: "Oui, et c'est rapide (quelques minutes). Le compte permet de s'inscrire aux prochains concours et de recevoir les notifications. Le compte Joueur est totalement gratuit. Une offre premium optionnelle (Joueur Plus) proposera des fonctionnalités avancées, sans rien retirer au compte gratuit.",
  },
  {
    q: 'Quels types de concours sont supportés ?',
    a: 'Tous les formats : tête-à-tête, doublette, triplette. Du niveau local au national. Pétanque, jeu provençal et boule lyonnaise.',
  },
  {
    q: 'BoulesConnect est-il compatible avec Gestion Concours FFPJP ?',
    a: 'Oui. Les inscriptions, et équipes peuvent être exportées dans un format compatible avec Gestion Concours FFPJP, pour que vous puissiez continuer à remonter vos résultats à la fédération sans double saisie.',
  },
];

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

export function TestimonialsSection() {
  return (
    <section id='temoignages' className='px-6 py-32 bg-slate-50 dark:bg-gray-950'>
      <div className='max-w-7xl mx-auto'>
        <div className='text-center mb-20'>
          <span className='text-blue-600 font-bold text-sm uppercase tracking-wider'>Témoignages</span>
          <h2 className='text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white mt-4 mb-6'>
            Ils ont adopté BoulesConnect
            <br />
            <span className='gradient-text'>et ne reviendront jamais en arrière</span>
          </h2>
        </div>
        <div className='grid md:grid-cols-3 gap-8'>
          {TESTIMONIALS.map(testimonial => (
            <div
              key={testimonial.name}
              className='bg-white dark:bg-slate-900 rounded-3xl p-8 border border-slate-200 dark:border-slate-700 hover-lift'
            >
              <div className='flex items-center gap-4 mb-6'>
                <div
                  className={`w-12 h-12 ${testimonial.color} rounded-full flex items-center justify-center text-white font-bold text-lg`}
                >
                  {testimonial.initials}
                </div>
                <div>
                  <h4 className='font-bold text-slate-900 dark:text-white'>{testimonial.name}</h4>
                  <p className='text-sm text-slate-500 dark:text-slate-400'>{testimonial.role}</p>
                </div>
              </div>
              <div className='flex gap-1 mb-4'>
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className='w-4 h-4 text-yellow-500 fill-yellow-500' />
                ))}
              </div>
              <p className='text-slate-600 dark:text-slate-400 italic'>"{testimonial.text}"</p>
            </div>
          ))}

          {/* CTA témoigner */}
          <div className='mt-16 rounded-3xl border border-blue-200 bg-blue-50 p-8 text-center dark:border-blue-800/50 dark:bg-blue-950/30 md:p-12'>
            <div className='mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-100 dark:bg-blue-900/50'>
              <MessageSquarePlus className='h-7 w-7 text-blue-600 dark:text-blue-400' />
            </div>
            <h3 className='text-xl font-extrabold text-slate-900 dark:text-white md:text-2xl'>
              Vous aussi, partagez votre expérience !
            </h3>
            <p className='mx-auto mt-3 max-w-lg text-slate-600 dark:text-slate-400'>
              Vous utilisez BoulesConnect pour vos concours ? Votre retour compte ! Envoyez-nous votre témoignage et
              aidez d'autres organisateurs et joueurs à découvrir la plateforme.
            </p>
            <Link
              to={ROUTES.CONTACT}
              className='mt-6 inline-flex items-center gap-2 rounded-full bg-blue-600 px-8 py-3 text-sm font-bold text-white shadow-lg shadow-blue-200 transition-all hover:bg-blue-700 hover:shadow-xl dark:shadow-blue-900/30'
            >
              Envoyer mon témoignage
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FaqSection() {
  return (
    <section id='faq' className='px-6 py-32 bg-slate-50 dark:bg-gray-950'>
      <JsonLd data={faqJsonLd} />
      <div className='max-w-4xl mx-auto'>
        <div className='text-center mb-20'>
          <span className='text-blue-600 font-bold text-sm uppercase tracking-wider'>FAQ</span>
          <h2 className='text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white mt-4 mb-6'>
            Questions fréquentes
          </h2>
        </div>
        <div className='space-y-4'>
          {FAQ.map(faq => (
            <details
              key={faq.q}
              className='bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-700 cursor-pointer hover:border-blue-300 dark:hover:border-blue-700 transition group'
            >
              <summary className='font-bold text-slate-900 dark:text-white text-lg flex items-center justify-between'>
                {faq.q}
                <ChevronDown className='w-5 h-5 text-slate-400 group-open:rotate-180 transition-transform flex-shrink-0' />
              </summary>
              <p className='mt-4 text-slate-600 dark:text-slate-400 leading-relaxed'>{faq.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
